import { useState, useRef, useCallback, type FormEvent, type DragEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useReqdeskContext } from '../ReqdeskProvider'
import { widgetCategoriesOptions, widgetTagsOptions } from '../queries'
import { submitTicket as apiSubmitTicket, uploadAttachment, createCategory as apiCreateCategory, createTag as apiCreateTag, type CategoryItem } from '../../api-client'
import { saveTrackingToken, saveWidgetEmail, loadWidgetEmail } from '../../storage'
import { collectAllMetadata, getMetadataPreferences, saveMetadataPreferences, collectDiagnosticMetadata, DIAGNOSTIC_FIELDS, type MetadataPreferences } from '../../client-metadata'
import { en } from '../../i18n/en'
import { ar } from '../../i18n/ar'
import type { TicketResult, WidgetError, SubmitTicketData } from '../types'
import { FormErrorProvider, FormFieldGroup } from '../form-errors'
import { formatFileSize, queuedFileId, validateAttachment, type QueuedFile } from './attachments'

const translations: Record<string, Record<string, string>> = { en, ar }

const MAX_FILES = 5

interface SubmitTicketViewProps {
  projectId: string
  onSuccess?: (result: TicketResult) => void
  onError?: (error: WidgetError) => void
  isAuthenticated?: boolean
  userEmail?: string
}

interface UploadProgress {
  fileId: string
  fileName: string
  percent: number
}

export function SubmitTicketView({ projectId, onSuccess, onError, isAuthenticated, userEmail }: SubmitTicketViewProps) {
  const ctx = useReqdeskContext()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const savedEmail = loadWidgetEmail(ctx.apiKey)

  // When the host app has already identified the customer (signed or unsigned), skip the email
  // prompt and use that identity directly. FR-022 (signed) and FR-023 (unsigned) mandate this UX.
  const preAuthedEmail =
    ctx.resolvedIdentityMode.kind === 'signed' || ctx.resolvedIdentityMode.kind === 'unsigned'
      ? ctx.resolvedIdentityMode.email
      : undefined
  const hideEmailField = preAuthedEmail !== undefined || isAuthenticated === true

  // Client-side validation map (title length, email format, etc). Merged with the server
  // errors through <FormErrorProvider>. Cleared on submit.
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({})
  const [files, setFiles] = useState<QueuedFile[]>([])
  const [isDragOver, setIsDragOver] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null)
  const [success, setSuccess] = useState<TicketResult | null>(null)
  const [rememberEmail, setRememberEmail] = useState(!!savedEmail)

  // Category selector state
  const [categoryPath, setCategoryPath] = useState<CategoryItem[]>([])
  const [selectedCategory, setSelectedCategory] = useState<CategoryItem | null>(null)
  const currentParentId = categoryPath.length > 0 ? categoryPath[categoryPath.length - 1].id : null
  const { data: categories = [] } = useQuery(widgetCategoriesOptions(projectId, currentParentId))

  // Tag selector state — multi-select toggle. Picker surface renders whenever we have *anything*
  // to show, which now includes the "add tag" affordance, so an empty-tags project still lets the
  // end user contribute a label instead of hiding the feature entirely.
  const [selectedTagIds, setSelectedTagIds] = useState<ReadonlySet<string>>(() => new Set())
  const { data: availableTags = [] } = useQuery(widgetTagsOptions(projectId))
  const toggleTag = useCallback((tagId: string) => {
    setSelectedTagIds(prev => {
      const next = new Set(prev)
      if (next.has(tagId)) next.delete(tagId)
      else next.add(tagId)
      return next
    })
  }, [])

  // Inline tag creation — mirrors the category add flow: "+ Add tag" chip toggles an inline input;
  // success auto-selects the new tag so the user's intent (to apply it) is honored without a
  // second click.
  const [newTagName, setNewTagName] = useState('')
  const [showNewTag, setShowNewTag] = useState(false)
  const [newTagError, setNewTagError] = useState<string | null>(null)
  const newTagMutation = useMutation({
    meta: { form: 'tag-create' as const },
    mutationFn: () => apiCreateTag(projectId, newTagName.trim()),
    onSuccess: (created) => {
      setNewTagName('')
      setShowNewTag(false)
      setNewTagError(null)
      setSelectedTagIds(prev => {
        const next = new Set(prev)
        next.add(created.id)
        return next
      })
      queryClient.invalidateQueries({ queryKey: ['widget-tags', projectId] })
    },
    onError: (err: unknown) => {
      const code = (err as { code?: string } | null)?.code ?? ''
      if (code === 'TAG_EXISTS') setNewTagError(t('tag.exists'))
      else setNewTagError(t('error.generic'))
    },
  })

  // Inline category creation
  const [newCatName, setNewCatName] = useState('')
  const [showNewCat, setShowNewCat] = useState(false)
  const [newCatError, setNewCatError] = useState<string | null>(null)

  // Category search — filters the current level (local to the loaded page). For projects with
  // hundreds of categories this keeps the list usable without needing a server-side search.
  const [catSearch, setCatSearch] = useState('')
  const [catActiveIndex, setCatActiveIndex] = useState(0)
  const filteredCategories = catSearch.trim()
    ? categories.filter(c => c.name.toLowerCase().includes(catSearch.trim().toLowerCase()))
    : categories
  // Reset cursor when the pool changes (level change, search change).
  const catListKey = `${currentParentId ?? 'root'}|${catSearch}`
  const prevCatListKey = useRef(catListKey)
  if (prevCatListKey.current !== catListKey) {
    prevCatListKey.current = catListKey
    if (catActiveIndex !== 0) setCatActiveIndex(0)
  }
  const newCatMutation = useMutation({
    meta: { form: 'category-create' as const },
    mutationFn: () => apiCreateCategory(projectId, newCatName.trim(), currentParentId),
    onSuccess: (created) => {
      setNewCatName('')
      setShowNewCat(false)
      setNewCatError(null)
      setSelectedCategory(created)
      queryClient.invalidateQueries({ queryKey: ['widget-categories', projectId] })
    },
    onError: (err: any) => {
      const code = err?.code ?? ''
      if (code === 'CATEGORY_EXISTS') setNewCatError(t('category.exists'))
      else if (code === 'HTTP_402' || code === 'SUBSCRIPTION_LIMIT_EXCEEDED') setNewCatError(t('category.limitReached'))
      else setNewCatError(t('error.generic'))
    },
  })

  // Diagnostic metadata state. The UI exposes a single master toggle ("share diagnostic info")
  // that flips every field in lockstep; the per-field storage model is retained so a future
  // admin UI can target individual fields without a migration. Derived values:
  //   - `diagAllChecked` drives the master checkbox's checked state.
  //   - `diagValues` is a read-only preview (all six fields forced on) used to show the user
  //     exactly what would be shared when they opt in.
  const [diagOpen, setDiagOpen] = useState(false)
  const [diagPrefs, setDiagPrefs] = useState<MetadataPreferences>(getMetadataPreferences(ctx.apiKey))
  const diagAllChecked = Object.values(diagPrefs).every(Boolean)
  const diagValues = collectDiagnosticMetadata({
    screenResolution: true, deviceType: true, timezone: true,
    referrerUrl: true, language: true, platform: true,
  })

  const t = useCallback((key: string): string => {
    if (ctx.translations?.[key]) return ctx.translations[key]
    const lang = translations[ctx.language] ?? translations.en
    return lang[key] ?? key
  }, [ctx.language, ctx.translations])

  const submitMutation = useMutation({
    // Tagging with `form: 'ticket-submit'` lets the global MutationCache.onError suppress its
    // toast for 422s — per-field errors flow through <FormErrorProvider> into each field's
    // aria-invalid + inline .rqd-error-text instead. Non-422 errors (network, 5xx) still toast.
    meta: { form: 'ticket-submit' as const },
    mutationFn: async ({ data, validFiles }: { data: SubmitTicketData; validFiles: QueuedFile[] }) => {
      const result = await apiSubmitTicket(projectId, data)
      if (result.trackingToken) saveTrackingToken(ctx.apiKey, result.trackingToken)

      // Upload attachments sequentially after ticket creation
      for (const queued of validFiles) {
        setUploadProgress({ fileId: queued.id, fileName: queued.file.name, percent: 0 })
        await uploadAttachment(result.id, queued.file, (percent) => {
          setUploadProgress({ fileId: queued.id, fileName: queued.file.name, percent })
        })
      }
      setUploadProgress(null)
      return result
    },
    onSuccess: (result) => {
      setSuccess(result)
      setClientErrors({})
      onSuccess?.(result)
    },
    onError: (err) => {
      setUploadProgress(null)
      onError?.(err as unknown as WidgetError)
    },
  })

  function addFiles(newFiles: FileList | File[]) {
    const toAdd: QueuedFile[] = []
    for (const file of Array.from(newFiles)) {
      if (files.length + toAdd.length >= MAX_FILES) break
      const { error } = validateAttachment(file, t)
      toAdd.push({ file, id: queuedFileId(), error: error ?? undefined })
    }
    setFiles(prev => [...prev, ...toAdd])
  }

  function removeFile(id: string) {
    setFiles(prev => prev.filter(f => f.id !== id))
  }

  function handleDragOver(e: DragEvent) { e.preventDefault(); setIsDragOver(true) }
  function handleDragLeave(e: DragEvent) { e.preventDefault(); setIsDragOver(false) }
  function handleDrop(e: DragEvent) {
    e.preventDefault(); setIsDragOver(false)
    if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files)
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = e.currentTarget
    const formData = new FormData(form)

    const title = (formData.get('title') as string)?.trim() ?? ''
    // Precedence: host-app pre-auth → authenticated SSO user email → form field → saved email.
    const email =
      preAuthedEmail
      ?? userEmail
      ?? (formData.get('email') as string)?.trim()
      ?? savedEmail
      ?? ''
    const newErrors: Record<string, string> = {}

    if (!title) newErrors.title = t('error.required')
    else if (title.length < 5) newErrors.title = t('error.titleMin')
    if (!hideEmailField && email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = t('error.emailInvalid')
    }

    if (Object.keys(newErrors).length > 0) { setClientErrors(newErrors); return }
    setClientErrors({})

    if (rememberEmail && email) saveWidgetEmail(ctx.apiKey, email)

    // Save diagnostic preferences
    saveMetadataPreferences(ctx.apiKey, diagPrefs)

    const validFiles = files.filter(f => !f.error)
    const data: SubmitTicketData = {
      title,
      description: (formData.get('description') as string)?.trim() || undefined,
      email,
      priority: (formData.get('priority') as SubmitTicketData['priority']) ?? 'medium',
      categoryId: selectedCategory?.id,
      clientMetadata: collectAllMetadata(ctx.apiKey),
      tagIds: selectedTagIds.size > 0 ? Array.from(selectedTagIds) : undefined,
    }

    submitMutation.mutate({ data, validFiles })
  }

  if (ctx.resolvedIdentityMode.kind === 'error') {
    return (
      <div className="rqd-placeholder" style={{ textAlign: 'center', padding: '24px 0' }}>
        <p style={{ marginBottom: 8 }}>{t('auth.noAuthConfigured')}</p>
        <p style={{ fontSize: 13, color: 'var(--rqd-text-secondary)' }}>
          {t('auth.noAuthConfiguredHint')}
        </p>
      </div>
    )
  }

  if (success) {
    return (
      <div className="rqd-success" style={{ textAlign: 'center', padding: '24px 0' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>{'\u2705'}</div>
        <h3 style={{ margin: '0 0 8px', fontSize: 18 }}>{t('success.title')}</h3>
        <p>{t('success.ticketNumber')}{success.ticketNumber}</p>
        {success.trackingToken && (
          <>
            <p style={{ fontSize: 13, color: 'var(--rqd-text-secondary)' }}>{t('success.trackingHint')}</p>
            <div className="rqd-token-box">{success.trackingToken}</div>
            <button className="rqd-btn rqd-btn-secondary" onClick={() => navigator.clipboard.writeText(success.trackingToken!)}>
              {t('success.copyToken')}
            </button>
          </>
        )}
      </div>
    )
  }

  if (submitMutation.isPending && uploadProgress) {
    return (
      <div style={{ padding: '24px 0', textAlign: 'center' }}>
        <p style={{ marginBottom: 12, fontWeight: 500 }}>{t('attach.uploading')}</p>
        <div className="rqd-file-item" style={{ marginBottom: 8 }}>
          <div className="rqd-file-item-info">
            <span className="rqd-file-item-name">{uploadProgress.fileName}</span>
          </div>
          <span style={{ fontSize: 12, color: 'var(--rqd-text-secondary)' }}>{uploadProgress.percent}%</span>
        </div>
        <div className="rqd-progress">
          <div className="rqd-progress-bar" style={{ width: `${uploadProgress.percent}%` }} />
        </div>
      </div>
    )
  }

  return (
    <FormErrorProvider mutation={submitMutation} clientErrors={clientErrors}>
      <form className="rqd-form" onSubmit={handleSubmit} noValidate>
      <FormFieldGroup name="title" label={t('form.title')}>
        <input
          className="rqd-input"
          name="title"
          placeholder={t('form.titlePlaceholder')}
          required
        />
      </FormFieldGroup>
      <FormFieldGroup name="description" label={t('form.description')}>
        <textarea
          className="rqd-textarea"
          name="description"
          placeholder={t('form.descriptionPlaceholder')}
        />
      </FormFieldGroup>
      {!hideEmailField && (
        <FormFieldGroup name="email" label={t('form.email')}>
          <div>
            <input
              className="rqd-input"
              name="email"
              type="email"
              placeholder={t('form.emailPlaceholder')}
              defaultValue={userEmail ?? savedEmail ?? ''}
            />
            <label className="rqd-checkbox-label" style={{ marginTop: 6 }}>
              <input type="checkbox" checked={rememberEmail} onChange={(e) => setRememberEmail(e.target.checked)} />
              {t('mytickets.rememberMe')}
            </label>
          </div>
        </FormFieldGroup>
      )}
      <FormFieldGroup name="priority" label={t('form.priority')}>
        <select
          className="rqd-select"
          name="priority"
          defaultValue="medium"
        >
          <option value="low">{t('form.priorityLow')}</option>
          <option value="medium">{t('form.priorityMedium')}</option>
          <option value="high">{t('form.priorityHigh')}</option>
          <option value="critical">{t('form.priorityCritical')}</option>
        </select>
      </FormFieldGroup>

      {/* Category Selector — searchable, scrollable tree navigator. Large projects have hundreds
          of categories, so we show a fixed-height scroll container with a sticky search/breadcrumb
          header. Nested creation happens at the current level (the `currentParentId` flows into
          the create mutation automatically). */}
      <div className="rqd-form-group">
        <label className="rqd-label">{t('form.categorySelected')}</label>
        {selectedCategory ? (
          <div className="rqd-category-selected">
            <span className="rqd-category-selected-path">
              {categoryPath.map(c => c.name).join(' \u203A ')}{categoryPath.length > 0 ? ' \u203A ' : ''}{selectedCategory.name}
            </span>
            <button
              type="button"
              className="rqd-category-selected-clear"
              aria-label={t('form.categoryClear')}
              onClick={() => { setSelectedCategory(null); setCategoryPath([]); setCatSearch('') }}
            >
              &times;
            </button>
          </div>
        ) : (
          <div className="rqd-category-picker">
            <div className="rqd-category-picker-header">
              <div className="rqd-category-breadcrumb">
                <button
                  type="button"
                  className="rqd-category-breadcrumb-crumb"
                  onClick={() => { setCategoryPath([]); setCatSearch('') }}
                  aria-current={categoryPath.length === 0 ? 'location' : undefined}
                >
                  {t('form.categoryRoot')}
                </button>
                {categoryPath.map((c, i) => (
                  <span key={c.id} className="rqd-category-breadcrumb-segment">
                    <span className="rqd-category-breadcrumb-sep" aria-hidden="true">&rsaquo;</span>
                    <button
                      type="button"
                      className="rqd-category-breadcrumb-crumb"
                      onClick={() => { setCategoryPath(prev => prev.slice(0, i + 1)); setCatSearch('') }}
                      aria-current={i === categoryPath.length - 1 ? 'location' : undefined}
                    >
                      {c.name}
                    </button>
                  </span>
                ))}
              </div>
              <input
                type="search"
                className="rqd-input rqd-category-search"
                value={catSearch}
                onChange={e => setCatSearch(e.target.value)}
                placeholder={t('form.categorySearch')}
                aria-label={t('form.categorySearch')}
                onKeyDown={e => {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault()
                    setCatActiveIndex(i => Math.min(i + 1, Math.max(filteredCategories.length - 1, 0)))
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault()
                    setCatActiveIndex(i => Math.max(i - 1, 0))
                  } else if (e.key === 'Enter') {
                    const cat = filteredCategories[catActiveIndex]
                    if (!cat) return
                    e.preventDefault()
                    if (cat.hasChildren) { setCategoryPath(prev => [...prev, cat]); setCatSearch('') }
                    else setSelectedCategory(cat)
                  } else if (e.key === 'Escape' && catSearch) {
                    e.preventDefault()
                    setCatSearch('')
                  }
                }}
              />
            </div>

            <div className="rqd-category-list" role="listbox" aria-label={t('form.categorySelected')}>
              {filteredCategories.length === 0 && (
                <span className="rqd-category-empty">
                  {catSearch
                    ? t('form.categoryNoMatch')
                    : (categoryPath.length === 0 ? t('form.categoryPlaceholder') : t('form.categoryLeaf'))}
                </span>
              )}
              {filteredCategories.map((cat, i) => (
                <button
                  key={cat.id}
                  type="button"
                  role="option"
                  aria-selected={i === catActiveIndex}
                  className={`rqd-category-item${i === catActiveIndex ? ' rqd-category-item-active' : ''}`}
                  onMouseEnter={() => setCatActiveIndex(i)}
                  onClick={() => {
                    if (cat.hasChildren) {
                      setCategoryPath(prev => [...prev, cat])
                      setCatSearch('')
                    } else {
                      setSelectedCategory(cat)
                    }
                  }}
                >
                  <span className="rqd-category-item-name">{cat.name}</span>
                  {cat.hasChildren && <span className="rqd-category-item-chevron" aria-hidden="true">&rsaquo;</span>}
                </button>
              ))}
            </div>

            {/* Inline add: creates a category at the current level. When nested, the button makes
                the target parent explicit so users know where the new category will land. */}
            <div className="rqd-category-add-row">
              {showNewCat ? (
                <div className="rqd-category-add-inline">
                  <input
                    type="text"
                    className="rqd-input rqd-category-add-input"
                    value={newCatName}
                    onChange={e => { setNewCatName(e.target.value); setNewCatError(null) }}
                    placeholder={
                      categoryPath.length > 0
                        ? `${t('category.namePlaceholder')} \u2014 ${categoryPath[categoryPath.length - 1].name}`
                        : t('category.namePlaceholder')
                    }
                    autoFocus
                    onKeyDown={e => {
                      if (e.key === 'Enter') { e.preventDefault(); if (newCatName.trim()) newCatMutation.mutate() }
                      if (e.key === 'Escape') { setShowNewCat(false); setNewCatName(''); setNewCatError(null) }
                    }}
                  />
                  <button
                    type="button"
                    className="rqd-category-add-confirm"
                    disabled={!newCatName.trim() || newCatMutation.isPending}
                    onClick={() => newCatMutation.mutate()}
                    aria-label={t('category.addNew')}
                  >
                    {newCatMutation.isPending ? '…' : '✓'}
                  </button>
                  <button
                    type="button"
                    className="rqd-category-add-cancel"
                    onClick={() => { setShowNewCat(false); setNewCatName(''); setNewCatError(null) }}
                    aria-label={t('form.categoryClear')}
                  >
                    ✕
                  </button>
                  {newCatError && <span className="rqd-category-add-error">{newCatError}</span>}
                </div>
              ) : (
                <button
                  type="button"
                  className="rqd-category-add-btn"
                  onClick={() => setShowNewCat(true)}
                >
                  <span aria-hidden="true" style={{ fontSize: 16, lineHeight: 1 }}>+</span>
                  {categoryPath.length > 0
                    ? `${t('category.addNew')} \u2014 ${t('category.addUnder')} ${categoryPath[categoryPath.length - 1].name}`
                    : t('category.addNew')}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tag picker — multi-select chips plus an inline "Add tag" affordance. Always rendered so
          even a project with zero tags shows the end user there's a label surface; each chip uses
          its configured color via the `--rqd-tag-color` custom property. */}
      <div className="rqd-form-group">
        <label className="rqd-label">{t('form.tags')}</label>
        <div className="rqd-tag-picker" role="group" aria-label={t('form.tags')}>
          {availableTags.map(tag => {
            const selected = selectedTagIds.has(tag.id)
            return (
              <button
                key={tag.id}
                type="button"
                className={`rqd-tag-chip${selected ? ' rqd-tag-chip-selected' : ''}`}
                aria-pressed={selected}
                onClick={() => toggleTag(tag.id)}
                style={{ ['--rqd-tag-color' as string]: tag.color }}
              >
                <span className="rqd-tag-chip-dot" aria-hidden="true" />
                {tag.name}
              </button>
            )
          })}
          {showNewTag ? (
            <span className="rqd-tag-add-inline" role="group" aria-label={t('tag.addNew')}>
              <input
                type="text"
                className="rqd-input rqd-tag-add-input"
                value={newTagName}
                onChange={e => { setNewTagName(e.target.value); setNewTagError(null) }}
                placeholder={t('tag.namePlaceholder')}
                maxLength={60}
                autoFocus
                onKeyDown={e => {
                  if (e.key === 'Enter') { e.preventDefault(); if (newTagName.trim()) newTagMutation.mutate() }
                  if (e.key === 'Escape') { e.preventDefault(); setShowNewTag(false); setNewTagName(''); setNewTagError(null) }
                }}
              />
              <button
                type="button"
                className="rqd-tag-add-confirm"
                disabled={!newTagName.trim() || newTagMutation.isPending}
                onClick={() => newTagMutation.mutate()}
                aria-label={t('tag.addNew')}
              >
                {newTagMutation.isPending ? '…' : '\u2713'}
              </button>
              <button
                type="button"
                className="rqd-tag-add-cancel"
                onClick={() => { setShowNewTag(false); setNewTagName(''); setNewTagError(null) }}
                aria-label={t('form.categoryClear')}
              >
                {'\u00d7'}
              </button>
            </span>
          ) : (
            <button
              type="button"
              className="rqd-tag-chip rqd-tag-chip-add"
              onClick={() => setShowNewTag(true)}
            >
              <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1 }}>+</span>
              {availableTags.length === 0 ? t('tag.addFirst') : t('tag.addNew')}
            </button>
          )}
        </div>
        {newTagError && <span className="rqd-tag-add-error">{newTagError}</span>}
      </div>

      {/* Diagnostic metadata — single opt-in toggle that flips every field in lockstep. The list
          beneath it is a read-only preview of what's included when shared, so the user sees the
          value they're granting before agreeing. Individual per-field control is kept in the
          storage model for future admin UIs; this surface is intentionally one toggle. */}
      <div className="rqd-form-group">
        <label className="rqd-diag-master">
          <input
            type="checkbox"
            checked={diagAllChecked}
            onChange={(e) => {
              const next = e.target.checked
              setDiagPrefs({
                screenResolution: next,
                deviceType: next,
                timezone: next,
                referrerUrl: next,
                language: next,
                platform: next,
              })
            }}
          />
          <span className="rqd-diag-master-label">
            <span className="rqd-diag-master-title">{t('diag.shareAll')}</span>
            <span className="rqd-diag-master-hint">{t('diag.hint')}</span>
          </span>
        </label>
        <button
          type="button"
          className="rqd-diag-details-toggle"
          onClick={() => setDiagOpen(prev => !prev)}
          aria-expanded={diagOpen}
        >
          <span>{diagOpen ? t('diag.hideIncluded') : t('diag.showIncluded')}</span>
          <span aria-hidden="true">{diagOpen ? '\u25B2' : '\u25BC'}</span>
        </button>
        {diagOpen && (
          <ul className="rqd-diag-preview">
            {DIAGNOSTIC_FIELDS.map(field => (
              <li key={field.key} className="rqd-diag-preview-item">
                <span className="rqd-diag-preview-label">{t(field.labelKey)}</span>
                <span className="rqd-diag-preview-value">{diagValues[field.key] ?? '—'}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rqd-form-group">
        <label className="rqd-label">{t('form.attachment')}</label>
        <div
          className={`rqd-dropzone${isDragOver ? ' rqd-dropzone-active' : ''}`}
          onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          {isDragOver ? t('attach.dropzoneActive') : t('attach.dropzone')}
          <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }}
            onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.target.value = '' }} />
        </div>
        {files.length > 0 && (
          <div className="rqd-file-list">
            {files.map(f => (
              <div key={f.id} className="rqd-file-item">
                <div className="rqd-file-item-info">
                  <span className="rqd-file-item-name">{f.file.name}</span>
                  <span className="rqd-file-item-size">
                    {formatFileSize(f.file.size)}
                    {f.error && <span style={{ color: '#e74c3c', marginLeft: 6 }}>{f.error}</span>}
                  </span>
                </div>
                <button className="rqd-file-remove" onClick={() => removeFile(f.id)} type="button">&times;</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <button className="rqd-btn rqd-btn-primary" type="submit" disabled={submitMutation.isPending}>
        {submitMutation.isPending ? t('form.submitting') : t('form.submit')}
      </button>
      </form>
    </FormErrorProvider>
  )
}
